import React, { useState, useEffect } from "react";
import { Modal, Button, Badge, ListGroup, Alert, Row, Col } from "react-bootstrap";
import axios from "axios";
import moment from "moment";
import { API_ENDPOINTS } from '../../config/api';
import { CalendarFold, Clock, MapPinHouse, Repeat2, Paperclip, Download, Info } from 'lucide-react';

export default function EventDetailsModal({ isOpen, onClose, event, onEventUpdated, onEventDeleted, onEditClick }) {
  const [attachments, setAttachments] = useState([]);
  const [attendees, setAttendees] = useState([]);
  const [loadingAttachments, setLoadingAttachments] = useState(false);
  const [loadingAttendees, setLoadingAttendees] = useState(false);

  useEffect(() => {
    if (isOpen && event) {
      // Only fetch attachments and attendees for non-subscription events
      if (!event.source || event.source !== 'subscription') {
        fetchAttachments();
        fetchAttendees();
      } else {
        // For subscription events, clear any existing data
        setAttachments([]);
        setAttendees([]);
      }
    }
  }, [isOpen, event]);

  const fetchAttachments = async () => {
    if (!event) return;
    
    // Skip fetching attachments for subscription events
    if (event.source === 'subscription') {
      console.log('Skipping attachments fetch for subscription event:', event.id, event.title);
      setAttachments([]);
      return;
    }
    
    try {
      setLoadingAttachments(true);
      console.log('Fetching attachments for event:', event.id, event.title);
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_ENDPOINTS.EVENT_ATTACHMENTS}/event/${event.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAttachments(response.data);
    } catch (error) {
      console.error("Error fetching attachments:", error);
    } finally {
      setLoadingAttachments(false);
    }
  };

  const fetchAttendees = async () => {
    if (!event) return;
    
    // Skip fetching attendees for subscription events
    if (event.source === 'subscription') {
      console.log('Skipping attendees fetch for subscription event:', event.id, event.title);
      setAttendees([]);
      return;
    }
    
    try {
      setLoadingAttendees(true);
      console.log('Fetching attendees for event:', event.id, event.title);
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_ENDPOINTS.EVENT_INVITATIONS}/event/${event.id}/attendees`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAttendees(response.data.attendees);
    } catch (error) {
      console.error("Error fetching attendees:", error);
    } finally {
      setLoadingAttendees(false);
    }
  };

  const handleDownloadAttachment = async (attachmentId, fileName) => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_ENDPOINTS.EVENT_ATTACHMENTS}/download/${attachmentId}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      // Create a download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading attachment:", error);
      alert("Failed to download attachment");
    }
  };

  const handleDeleteAttachment = async (attachmentId) => {
    if (!window.confirm("Are you sure you want to delete this attachment?")) return;

    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API_ENDPOINTS.EVENT_ATTACHMENTS}/${attachmentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchAttachments(); // Refresh the list
    } catch (error) {
      console.error("Error deleting attachment:", error);
      alert("Failed to delete attachment");
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDate = (date) => {
    return moment.utc(date).local().format("MMM DD, YYYY h:mm A");
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge bg="warning">Pending</Badge>;
      case 'accepted':
        return <Badge bg="success">Accepted</Badge>;
      case 'declined':
        return <Badge bg="danger">Declined</Badge>;
      default:
        return <Badge bg="secondary">{status}</Badge>;
    }
  };

  // Determine if this user is the original creator or an attendee
  const currentUserId = parseInt(localStorage.getItem('userId') || '0');
  const isEventOwner = event?.userId === currentUserId;
  const isOriginalEvent = event && !event.originalEventId; // Event has no originalEventId, so it's the original
  const isOriginalCreator = isEventOwner && isOriginalEvent;

  const handleDelete = async () => {
    let confirmMessage;
    if (isOriginalCreator) {
      confirmMessage = "Are you sure you want to delete this event? This will delete the event for ALL attendees.";
    } else {
      confirmMessage = "Are you sure you want to remove this event from your calendar? This will only affect your view.";
    }

    if (!window.confirm(confirmMessage)) return;

    try {
      const token = localStorage.getItem("token");
      const response = await axios.delete(`${API_ENDPOINTS.EVENTS}/${event.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Show appropriate success message based on the response
      if (response.data.deletedOriginal) {
        alert(`Event deleted successfully for all attendees. Deleted ${response.data.deletedCopies} copies.`);
      } else {
        alert("Event removed from your calendar successfully.");
      }
      
      onEventDeleted(event.id);
      onClose();
    } catch (error) {
      console.error("Error deleting event:", error);
      alert("Failed to delete event. Please try again.");
    }
  };

  const handleDeleteSeries = async () => {
    let confirmMessage;
    if (isOriginalCreator) {
      confirmMessage = "Are you sure you want to delete this entire series? This will delete ALL events in the series for ALL attendees.";
    } else {
      confirmMessage = "Are you sure you want to remove this entire series from your calendar? This will only affect your view.";
    }

    if (!window.confirm(confirmMessage)) return;

    try {
      const token = localStorage.getItem("token");
      const response = await axios.delete(`${API_ENDPOINTS.EVENTS}/series/${event.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Show appropriate success message based on the response
      if (response.data.deletedCopies !== undefined) {
        alert(`Series deleted successfully for all attendees. Deleted ${response.data.deletedCount} events and ${response.data.deletedCopies} copies.`);
      } else {
        alert(`Series removed from your calendar successfully. Deleted ${response.data.deletedCount} events.`);
      }
      
      onEventDeleted(event.id);
      onClose();
    } catch (error) {
      console.error("Error deleting event series:", error);
      alert("Failed to delete event series. Please try again.");
    }
  };

  if (!event) return null;

  return (
    <Modal show={isOpen} onHide={onClose} centered size="lg">
      <Modal.Header closeButton className="border-bottom-0 px-4">
        <Modal.Title>
          <div className="d-flex flex-column">
            <div>
          {event.title}
          {event.isWeekendCall ? (
            <Badge bg="danger" className="ms-2">
              Weekend Call
            </Badge>
          ) : event.label && (
            <Badge bg="secondary" className="ms-2">
              {event.label}
            </Badge>
          )}
          </div>
          <div className="text-hl"></div>
          </div>
        </Modal.Title>
        
      </Modal.Header>
      <Modal.Body className="px-4">
        <Row>
          <Col md={6}>
            <div className="mb-3 d-flex flex-row gap-2">
          <div className="d-flex align-items-center gap-2" style={{ minWidth: '110px' }}> <CalendarFold/> <span className="fw-semibold">Date:</span></div>
          <div>
            {event.allDay
              ? moment(event.start).format("MMMM D, YYYY")
              : `${moment(event.start).format("MMMM D, YYYY")}`}
          </div>
        </div>
        <div className="mb-3 d-flex flex-row gap-2">
          <div className="d-flex align-items-center gap-2" style={{ minWidth: '110px' }}><Clock/> <span className="fw-semibold">Time:</span></div>
          <div>
            {event.allDay
              ? 'All Day Event'
              : `${moment.utc(event.start).local().format("h:mm A")} - ${moment.utc(event.end).local().format("h:mm A")}`}
          </div>
        </div>
        
          {event.location && (
            <>
            <div className="mb-3 d-flex flex-row gap-2">
              <div className="d-flex align-items-center gap-2" style={{ minWidth: '110px' }}><MapPinHouse/> <span className="fw-semibold">Location:</span></div>
              <div>{event.location}</div>
            </div>
            </>
          )}
          {event.repeat !== "none" && (
            <div className="mb-3 d-flex flex-row gap-2">
              <div className="fw-semibold" style={{ minWidth: '110px' }}>
                <Repeat2/> <span className="fw-semibold">Repeats:</span>
                Repeats: {event.repeat}
              </div>
            </div>
          )}
        
          </Col>
          <Col md={6}>
             {/* Attachments Section */}
        <div className="mb-3">
          <div className="fw-semibold">Attachments</div>
          {loadingAttachments ? (
            <div className="text-muted">Loading attachments...</div>
          ) : attachments.length > 0 ? (
            <ListGroup>
              {attachments.map((attachment) => (
                <ListGroup.Item key={attachment.id} className="d-flex justify-content-between align-items-center">
                  <div className="d-flex align-items-center gap-2">
                    <Paperclip/> <span className="fw-semibold">Attachment:</span>
                    {attachment.originalName}
                    <small className="text-muted ms-2">({formatFileSize(attachment.fileSize)})</small>
                  </div>
                  <div className="d-flex gap-2">
                    <Button
                      variant="outline-primary"
                      size="sm"
                      onClick={() => handleDownloadAttachment(attachment.id, attachment.originalName)}
                    >
                      <Download/>
                    </Button>
                  </div>
                </ListGroup.Item>
              ))}
            </ListGroup>
          ) : (
            <div className="text-muted">No attachments</div>
          )}
        </div>
          </Col>
        </Row>
        
        <div>
          {event.description && (
            <>
              <div className="fw-semibold mb-2">Description:</div>
              <div className="bg-light p-3 rounded w-100 mb-3">{event.description}</div>
            </>
          )}
          {event.source === 'subscription' && (
            <div className="alert alert-info mt-3">
              <Info className="me-2"/>
              This is a subscription event from <strong>{event.subscriptionName || 'an external calendar'}</strong>. 
              Subscription events cannot be edited or deleted from this calendar.
            </div>
          )}
        </div>

       

        {/* Attendees Section */}
        <div className="mb-3">
          <h6 className="fw-semibold">Attendees ({attendees.length})</h6>
          {loadingAttendees ? (
            <div className="text-muted">Loading attendees...</div>
          ) : attendees.length > 0 ? (
            <div className="d-flex flex-wrap gap-2">
              {attendees.map((attendee) => {
                let badgeBg = 'secondary';
                let icon = <i className="bi bi-clock"></i>;
                if (attendee.status === 'accepted') {
                  badgeBg = 'success';
                  icon = <i className="bi bi-check-circle-fill"></i>;
                } else if (attendee.status === 'declined') {
                  badgeBg = 'danger';
                  icon = <i className="bi bi-x-circle-fill"></i>;
                }
                return (
                  <span key={attendee.id} className={`badge rounded bg-${badgeBg} d-flex align-items-center gap-2 p-2`} style={{ fontSize: '1em' }}>
                    <span className="fw-normal">{attendee.firstName} {attendee.lastName}</span>
                    <span className="small">({attendee.role ? attendee.role : 'N/A'})</span>
                    {icon}
                    {attendee.isCreator && <span className="ms-2 badge rounded small py-1 px-2 bg-white text-muted fw-semibold">Creator</span>}
                  </span>
                );
              })}
            </div>
          ) : (
            <div className="text-muted">No attendees</div>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer className="border-top-0 px-4">
        <Button variant="light" size="sm" onClick={onClose}>
          Close
        </Button>
        {event.source !== 'subscription' && (
          <>
            <Button 
              variant="primary" 
              size="sm"
              onClick={() => {
                onEditClick();
                onClose();
              }}
            >
              Edit
            </Button>
            {event.repeat !== "none" && (
              <Button variant="warning" size="sm" onClick={handleDeleteSeries}>
                {(() => {
                  // Count attendees excluding the creator
                  const attendeesExcludingCreator = attendees.filter(attendee => !attendee.isCreator);
                  if (attendees.length === 0 || attendeesExcludingCreator.length === 0) {
                    return 'Delete Series';
                  } else if (attendees.length > 2) {
                    return 'Delete Series for All';
                  } else {
                    return 'Remove Series from Calendar';
                  }
                })()}
              </Button>
            )}
            <Button variant="danger" size="sm" onClick={handleDelete}>
              {(() => {
                // Count attendees excluding the creator
                const attendeesExcludingCreator = attendees.filter(attendee => !attendee.isCreator);
                
                if (attendeesExcludingCreator.length === 0) {
                  // No attendees other than creator (or no attendees at all)
                  return 'Delete';
                } else if (isOriginalCreator) {
                  // Has other attendees and is creator
                  return 'Delete for All';
                } else {
                  // Has other attendees but is not creator
                  return 'Remove from Calendar';
                }
              })()}
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
}